import {
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  SelectProps,
} from '@mui/material';
import { FC, useMemo } from 'react';

import useUser from '../../../hooks/useUser';
import TenantField from '../../../interfaces/TenantField';

interface ProductHierarchySelectProps extends Omit<SelectProps, 'onChange'> {
  onChange?: (hierarchy: TenantField) => void;
  value?: TenantField;
  options?: TenantField[];
  multiple: false;
}
interface ProductHierarchyMultiSelectProps
  extends Omit<SelectProps, 'onChange'> {
  onChange?: (hierarchy: TenantField[]) => void;
  value?: TenantField[];
  multiple: true;
  options?: TenantField[];
  minSelected?: number;
}

const ProductHierarchySelect: FC<
  ProductHierarchySelectProps | ProductHierarchyMultiSelectProps
> = ({ value, onChange, options, ...restProps }) => {
  const { selectedBusiness } = useUser();
  const productHierarchyMap = useMemo(() => {
    return selectedBusiness?.productHierarchy.reduce((acc, val) => {
      acc[val.value] = val;
      return acc;
    }, {} as Record<string, TenantField>);
  }, [selectedBusiness]);

  const selectedValue = Array.isArray(value)
    ? value.map((value) => value.value)
    : (value?.value || '');

  const handleMultipleOnChange = (newValues: any[]) => {
    // minimuim selected contraint
    if (
      restProps.multiple &&
      newValues.length >=
        (restProps.minSelected !== undefined ? restProps.minSelected : 0) // considering default min value as 0
    ) {
      onChange?.(
        newValues.map(
          (newValue: string) => productHierarchyMap![newValue] as TenantField
        ) as any
      );
    }
  };

  const handleOnChange = ({ target }: SelectChangeEvent<unknown>) => {
    if (Array.isArray(target.value) && restProps.multiple)
      handleMultipleOnChange(target.value);
    else if (!restProps.multiple)
      onChange?.(productHierarchyMap![target.value as string] as any);
  };

  const label = restProps.label || 'Hierarchy'

  return (
    <FormControl>
      <InputLabel id='product-hierarchy-select'>{label}</InputLabel>
      <Select
        {...restProps}
        labelId='product-hierarchy-select'
        defaultValue={selectedValue}
        value={selectedValue}
        onChange={handleOnChange}
        input={<OutlinedInput label={label} value={selectedValue} />}
        MenuProps={{
          slotProps: {
            paper: {
              sx: {
                minWidth: 200,
              },
            },
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }}
      >
        {(options === undefined
          ? selectedBusiness?.productHierarchy ?? []
          : options
        ).map(({ key, label, value }) => (
          <MenuItem
            key={key}
            value={value}
          >
            {label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
export default ProductHierarchySelect;
